import { Icon, InlineIcon } from "@iconify/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { deleteAttachment } from "../../api/deleteAttachment";
import { getAttachments } from "../../api/getAttachments";
import { AttachmentItem } from "./AttachmentItem";

interface AttachmentsModalProps {
  chatSessionId: string;
  isDarkMode: boolean;
  onClose: () => void;
}

export function AttachmentsModal({ chatSessionId, isDarkMode, onClose }: AttachmentsModalProps) {
  const queryClient = useQueryClient();
  const { data: attachmentsData, isLoading } = useQuery({
    queryKey: ["chatAttachments", chatSessionId],
    queryFn: () => getAttachments(chatSessionId),
    enabled: !!chatSessionId,
    refetchOnWindowFocus: false,
  });

  const attachments = attachmentsData?.data?.attachments ?? [];

  const deleteAttachmentMutation = useMutation({
    mutationFn: ({
      chat_session_uid,
      attachment_uid,
    }: {
      chat_session_uid: string;
      attachment_uid: string;
    }) => deleteAttachment(chat_session_uid, attachment_uid),
    onSuccess: () => {
      toast.success("Attachment deleted successfully.");
      queryClient.invalidateQueries({ queryKey: ["chatAttachments", chatSessionId] });
    },
    onError: (error) => {
      const errorMessage =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Failed to delete attachment. Please try again.";
      toast.error(errorMessage);
    },
  });

  const handleDelete = (attachmentUid: string) => {
    deleteAttachmentMutation.mutate({
      chat_session_uid: chatSessionId,
      attachment_uid: attachmentUid,
    });
  };

  return (
    <div className="Modal AttachmentsModal" data-dark-mode={isDarkMode}>
      <div
        className={[
          "Modal__card",
          "AttachmentsModal__card",
          isDarkMode ? "bg-gray-800" : "bg-white",
        ].join(" ")}
      >
        <div
          className={[
            "Modal__card__header",
            "AttachmentsModal__card__header",
            "rounded-t",
            isDarkMode ? "bg-gray-700" : "bg-gray-100",
          ].join(" ")}
        >
          <h6 className={isDarkMode ? "text-gray-100" : "text-gray-900"}>
            Quotation Request Chat Attachments
          </h6>
          <InlineIcon
            icon="ei:close"
            className={[
              "Modal__card__header__icon",
              isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-700 hover:text-gray-900",
            ].join(" ")}
            onClick={onClose}
          />
        </div>
        <div
          className={[
            "Modal__card__body",
            "AttachmentsModal__card__body",
            "rounded-b",
            isDarkMode ? "bg-gray-800" : "bg-white",
          ].join(" ")}
        >
          {isLoading ? (
            <p className={isDarkMode ? "text-gray-300" : "text-gray-700"}>Loading attachments...</p>
          ) : attachments.length === 0 ? (
            <p className={isDarkMode ? "text-gray-300" : "text-gray-700"}>No attachments found</p>
          ) : (
            <div
              className={[
                "grid",
                "grid-cols-1",
                "gap-2",
                "overflow-y-auto",
                "p-2",
                "max-h-[60vh]",
              ].join(" ")}
              style={{ scrollbarWidth: "thin" }}
            >
              {attachments.map((attachment) => (
                <div
                  key={attachment.uid}
                  className={["grid", "grid-cols-[1fr_auto]", "gap-2", "items-center"].join(" ")}
                >
                  <AttachmentItem attachment={attachment} isDarkMode={isDarkMode} />
                  <button
                    type="button"
                    onClick={() => handleDelete(attachment.uid)}
                    disabled={deleteAttachmentMutation.isLoading}
                    className={[
                      "grid",
                      "place-items-center",
                      "p-2",
                      "rounded-lg!",
                      "transition-colors",
                      "cursor-pointer",
                      "border-0",
                      "disabled:opacity-50",
                      "disabled:cursor-not-allowed",
                      isDarkMode
                        ? "text-red-400! hover:bg-red-500 hover:text-white!"
                        : "text-red-500! hover:bg-red-500 hover:text-white!",
                    ].join(" ")}
                    title="Delete attachment"
                  >
                    <Icon icon="mdi:delete-outline" className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
